<script>
  /**
   * The OAuth consent screen.
   *
   * An AI client has asked Hydra for a token on this user's behalf; Hydra has
   * authenticated them and handed the decision back to us. This page is the only
   * place a human sees what is about to be granted, so it errs towards showing
   * more than it hides — in particular the scopes the backend will *refuse*
   * because of the user's role, which would otherwise surface much later as a
   * tool that mysteriously fails.
   *
   * It stands outside `Layout` on purpose: the user is mid-handshake with a
   * third party, and the application chrome would invite them to wander off with
   * an authorization request left open.
   */
  import { onMount } from 'svelte';
  import { oauthApi, API_ROOT } from '../stores/api.js';
  import { consentChallengeFrom, consentLoginHref } from '../lib/oauthReturnTo.js';
  import {
    classifyOAuthFailure, missingChallengeFailure, missingRedirectFailure,
  } from '../lib/oauthFailure.js';
  import OAuthScopeList from '../components/OAuthScopeList.svelte';
  import { _ } from '../lib/i18n.js';
  import { theme } from '../stores/theme.js';
  import {
    ShieldCheck, Loader, AlertCircle, Ban, Check, X, LogIn, RefreshCw, Server,
  } from 'lucide-svelte';

  // Read straight from `window.location.search`, not from svelte-spa-router's
  // `querystring` store: the router is hash-based and its store is derived from
  // what follows the '#', while Hydra's challenge arrives before it.
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const challenge = consentChallengeFrom(search);
  // Back to login without losing the handshake: the challenge is rewritten into
  // a returnTo, which Login.svelte re-validates against the API base before
  // following.
  const loginHref =
    typeof window !== 'undefined'
      ? consentLoginHref(search, API_ROOT, window.location.pathname)
      : '#/login';

  let loading = true;
  /** @type {''|'approve'|'deny'} */
  let submitting = '';
  let redirecting = false;
  /** @type {Object|null} */
  let details = null;
  /** @type {import('../lib/oauthFailure.js').OAuthFailure|null} */
  let failure = null;

  onMount(() => {
    theme.init();
    if (!challenge) {
      failure = missingChallengeFailure();
      loading = false;
      return;
    }
    loadDetails();
  });

  async function loadDetails() {
    loading = true;
    failure = null;
    try {
      details = await oauthApi.consentDetails(challenge);
    } catch (error) {
      console.error('[OAuthConsent] Error loading consent details:', error);
      details = null;
      failure = classifyOAuthFailure(error);
    } finally {
      loading = false;
    }
  }

  /**
   * Record the decision and hand the browser back to Hydra.
   * @param {boolean} approve
   */
  async function decide(approve) {
    if (submitting || redirecting) return;
    submitting = approve ? 'approve' : 'deny';
    failure = null;
    try {
      const result = await oauthApi.submitConsent(challenge, approve);
      if (result?.redirectTo) {
        // A full navigation, not a router push: the destination belongs to
        // Hydra and then to the client's redirect URI.
        redirecting = true;
        window.location.assign(result.redirectTo);
        return;
      }
      failure = missingRedirectFailure();
    } catch (error) {
      console.error('[OAuthConsent] Error submitting consent:', error);
      failure = classifyOAuthFailure(error);
    }
    submitting = '';
  }

  $: clientName = details?.client?.name || '';
  $: grantable = details?.grantableScopes ?? [];
  $: denied = details?.deniedScopes ?? [];
  $: audience = details?.audience ?? [];
  $: canApprove = grantable.length > 0;
</script>

<div
  class="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 retro-grid transition-colors duration-300"
  style="background-color: var(--color-bg-dark);"
>
  <!-- Background decorations - retro glow orbs -->
  <div class="absolute inset-0 overflow-hidden pointer-events-none">
    <div class="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl" style="background: rgba(0, 240, 255, 0.08);"></div>
    <div class="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl" style="background: rgba(255, 0, 255, 0.08);"></div>
  </div>

  <div class="relative z-10 sm:mx-auto sm:w-full sm:max-w-lg fade-in">
    <div class="flex justify-center mb-8">
      <div class="relative scale-in">
        <div class="absolute inset-0 blur-2xl rounded-full" style="background: rgba(0, 240, 255, 0.2);"></div>
        <img class="relative h-16 w-auto drop-shadow-lg" src="/webdeploy-icon.png" alt="WEBDEPLOY" />
      </div>
    </div>
    <div class="text-center slide-up">
      <h1 class="text-3xl font-bold text-gradient font-display tracking-widest">{$_('oauth.consent.title')}</h1>
      <p class="mt-2 text-sm" style="color: var(--color-text-muted);">{$_('oauth.consent.subtitle')}</p>
    </div>
  </div>

  <div class="relative z-10 mt-10 sm:mx-auto sm:w-full sm:max-w-lg slide-up">
    <div class="card-elevated p-8">
      {#if loading}
        <div class="py-10 text-center">
          <Loader size={40} class="mx-auto text-primary-500 animate-spin mb-4" />
          <p class="text-sm" style="color: var(--color-text-muted);">{$_('common.loading')}</p>
        </div>

      {:else if failure}
        <div class="space-y-5">
          <div class="flex items-start gap-3">
            <div class="p-2 rounded-xl bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 flex-shrink-0">
              {#if failure.kind === 'unavailable'}
                <Server size={22} />
              {:else if failure.kind === 'sessionExpired'}
                <LogIn size={22} />
              {:else}
                <AlertCircle size={22} />
              {/if}
            </div>
            <div class="min-w-0">
              <h2 class="text-lg font-bold text-gray-900 dark:text-white">
                {$_(`oauth.errors.${failure.kind}Title`)}
              </h2>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {$_(`oauth.errors.${failure.kind}Body`)}
              </p>
              {#if failure.message}
                <p class="mt-2 text-xs text-gray-400 dark:text-gray-500 break-words">{failure.message}</p>
              {/if}
            </div>
          </div>

          {#if failure.kind === 'invalidScope' && failure.unsupportedScopes.length > 0}
            <div class="border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
              <p class="text-sm font-bold text-amber-800 dark:text-amber-200 mb-2">
                {$_('oauth.errors.unsupportedScopesTitle')}
              </p>
              <ul class="space-y-1">
                {#each failure.unsupportedScopes as scope (scope)}
                  <li class="font-mono text-xs text-amber-700 dark:text-amber-300 break-all">{scope}</li>
                {/each}
              </ul>
            </div>
          {/if}

          <div class="flex flex-col sm:flex-row gap-3 pt-2">
            {#if failure.kind === 'sessionExpired'}
              <a href={loginHref} class="btn btn-primary flex-1 h-11 flex items-center justify-center gap-2">
                <LogIn size={18} />
                <span>{$_('oauth.errors.backToLogin')}</span>
              </a>
            {:else if failure.kind === 'unavailable' || failure.kind === 'generic'}
              <button class="btn btn-primary flex-1 h-11 flex items-center justify-center gap-2" on:click={loadDetails}>
                <RefreshCw size={18} />
                <span>{$_('oauth.errors.retry')}</span>
              </button>
            {/if}
            {#if challenge && failure.kind !== 'missingChallenge' && failure.kind !== 'expiredChallenge'}
              <button
                class="btn btn-secondary flex-1 h-11 flex items-center justify-center gap-2"
                on:click={() => decide(false)}
                disabled={submitting !== '' || redirecting}
              >
                <X size={18} />
                <span>{$_('oauth.consent.deny')}</span>
              </button>
            {/if}
          </div>
        </div>

      {:else if details}
        <div class="space-y-6">
          <!-- Who is asking -->
          <div class="flex items-start gap-3">
            <div class="p-3 rounded-2xl shadow-lg flex-shrink-0" style="background: linear-gradient(to bottom right, #00bfff, #0090cc);">
              <ShieldCheck size={24} class="text-white" />
            </div>
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-widest" style="color: var(--color-text-muted);">
                {$_('oauth.consent.clientLabel')}
              </p>
              <h2 class="text-xl font-bold text-gray-900 dark:text-white break-words">{clientName}</h2>
              {#if details.client?.id && details.client.id !== clientName}
                <code class="text-xs text-gray-400 dark:text-gray-500 font-mono break-all">{details.client.id}</code>
              {/if}
            </div>
          </div>

          <!-- What will be granted -->
          <div>
            <h3 class="text-sm font-bold uppercase tracking-widest mb-3" style="color: var(--color-text-heading);">
              {$_('oauth.consent.grantedTitle')}
            </h3>
            {#if grantable.length > 0}
              <OAuthScopeList scopes={grantable} tone="granted" />
            {:else}
              <p class="text-sm text-gray-500 dark:text-gray-400">{$_('oauth.consent.nothingGrantable')}</p>
            {/if}
          </div>

          <!-- What will NOT be granted: shown, never quietly dropped -->
          {#if denied.length > 0}
            <div class="border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
              <div class="flex items-start gap-2 mb-3">
                <Ban size={18} class="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 class="text-sm font-bold text-amber-800 dark:text-amber-200">{$_('oauth.consent.deniedTitle')}</h3>
                  <p class="text-xs text-amber-700 dark:text-amber-300">{$_('oauth.consent.deniedHint')}</p>
                </div>
              </div>
              <OAuthScopeList scopes={denied} tone="denied" />
            </div>
          {/if}

          <!-- Where the token may be used -->
          {#if audience.length > 0}
            <div class="pt-2 border-t-2" style="border-color: var(--color-border-default);">
              <p class="text-xs uppercase tracking-widest mb-1" style="color: var(--color-text-muted);">
                {$_('oauth.consent.audienceLabel')}
              </p>
              {#each audience as target (target)}
                <code class="block text-xs font-mono text-gray-500 dark:text-gray-400 break-all">{target}</code>
              {/each}
            </div>
          {/if}

          <!-- The decision -->
          <div class="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              class="btn btn-primary flex-1 h-12 flex items-center justify-center gap-2"
              on:click={() => decide(true)}
              disabled={submitting !== '' || redirecting || !canApprove}
              title={canApprove ? '' : $_('oauth.consent.nothingGrantable')}
            >
              {#if submitting === 'approve'}
                <Loader size={18} class="animate-spin" />
              {:else}
                <Check size={18} />
              {/if}
              <span>{$_('oauth.consent.approve')}</span>
            </button>
            <button
              class="btn btn-secondary flex-1 h-12 flex items-center justify-center gap-2"
              on:click={() => decide(false)}
              disabled={submitting !== '' || redirecting}
            >
              {#if submitting === 'deny'}
                <Loader size={18} class="animate-spin" />
              {:else}
                <X size={18} />
              {/if}
              <span>{$_('oauth.consent.deny')}</span>
            </button>
          </div>

          <p class="text-xs text-center" style="color: var(--color-text-muted);">{$_('oauth.consent.footerHint')}</p>
        </div>
      {/if}
    </div>
  </div>
</div>
